import { PostgrestResponse } from '@supabase/supabase-js';
import { IVwMovimentacoesRecentes } from '../../../types/views/IVwMovimentacoesRecentes';
import { IVwProdutosMaisVendidos } from '../../../types/views/IVwProdutosMaisVendidos';
import { IVwSaldosAlunos } from '../../../types/views/IVwSaldosAlunos';
import { IVwSaldosResponsaveis } from '../../../types/views/IVwSaldosResponsaveis';
import { IVwVendasHoje } from '../../../types/views/IVwVendasHoje';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

// Tipos para os retornos customizados dos relatórios
interface DashboardGeral {
     vendas_hoje: IVwVendasHoje[] | null;
     saldos_responsaveis: IVwSaldosResponsaveis[] | null;
     saldos_alunos: IVwSaldosAlunos[] | null;
     produtos_mais_vendidos: IVwProdutosMaisVendidos[] | null;
     movimentacoes_recentes: IVwMovimentacoesRecentes[] | null;
}

interface EstatisticasRapidas {
     vendas_hoje: number;
     total_responsaveis: number;
     total_alunos: number;
     produtos_disponiveis: number;
}

/**
 * ViewRelatorios - Agrupamento de relatórios usando múltiplas views otimizadas
 * Este serviço combina consultas de diferentes views para gerar relatórios completos
 */
export class ViewRelatorios extends BaseMultiTenantService<any> {
     constructor() {
          super('vw_vendas_hoje', [], 'organizacao'); // View como tabela base
     }

     /**
      * Dashboard principal com métricas gerais
      */
     async getDashboardGeral(organizationId: number): Promise<DashboardGeral> {
          const [
               vendasHoje,
               saldosResponsaveis,
               saldosAlunos,
               produtosMaisVendidos,
               movimentacoesRecentes
          ] = await Promise.all([
               this.client.from('vw_vendas_hoje').select('*').eq('organizacao', organizationId),
               this.client.from('vw_saldos_responsaveis').select('*').eq('organizacao', organizationId),
               this.client.from('vw_saldos_alunos').select('*').eq('organizacao', organizationId),
               this.client.from('vw_produtos_mais_vendidos').select('*').eq('organizacao', organizationId).limit(5),
               this.client.from('vw_movimentacoes_recentes').select('*').eq('fk_id_organizacao', organizationId).limit(10)
          ]);

          return {
               vendas_hoje: vendasHoje.data,
               saldos_responsaveis: saldosResponsaveis.data,
               saldos_alunos: saldosAlunos.data,
               produtos_mais_vendidos: produtosMaisVendidos.data,
               movimentacoes_recentes: movimentacoesRecentes.data
          };
     }

     /**
      * Relatório de vendas usando view otimizada
      */
     async getRelatorioVendas(organizationId: number, filtros?: {
          dataInicio?: string;
          dataFim?: string;
          alunoId?: number;
          responsavelId?: number;
     }): Promise<PostgrestResponse<IVwVendasHoje[]>> {
          let query = this.client
               .from('vw_vendas_hoje')
               .select('*')
               .eq('organizacao', organizationId);

          if (filtros?.dataInicio) {
               query = query.gte('data_venda', filtros.dataInicio);
          }

          if (filtros?.dataFim) {
               query = query.lte('data_venda', filtros.dataFim);
          }

          if (filtros?.alunoId) {
               query = query.eq('id_aluno', filtros.alunoId);
          }

          if (filtros?.responsavelId) {
               query = query.eq('id_responsavel', filtros.responsavelId);
          }

          return await query.order('data_venda', { ascending: false });
     }

     /**
      * Relatório de consumo por aluno
      */
     async getRelatorioConsumoAlunos(organizationId: number): Promise<PostgrestResponse<IVwSaldosAlunos[]>> {
          return await this.client
               .from('vw_relatorio_consumo_aluno')
               .select('*')
               .eq('organizacao', organizationId)
               .order('valor_total_gasto', { ascending: false });
     }

     /**
      * Relatório de produtos mais vendidos
      */
     async getRelatorioProdutosMaisVendidos(organizationId: number, limite: number = 20): Promise<PostgrestResponse<IVwProdutosMaisVendidos[]>> {
          return await this.client
               .from('vw_produtos_mais_vendidos')
               .select('*')
               .eq('organizacao', organizationId)
               .order('quantidade_total', { ascending: false })
               .limit(limite);
     }

     /**
      * Relatório de saldos consolidado
      */
     async getRelatorioSaldos(organizationId: number): Promise<{ saldos_alunos: IVwSaldosAlunos[] | null; saldos_responsaveis: IVwSaldosResponsaveis[] | null; }> {
          const [responsaveis, alunos] = await Promise.all([
               this.client.from('vw_saldos_responsaveis').select('*').eq('organizacao', organizationId),
               this.client.from('vw_saldos_alunos').select('*').eq('organizacao', organizationId)
          ]);

          return {
               saldos_responsaveis: responsaveis.data,
               saldos_alunos: alunos.data
          };
     }

     /**
      * Relatório de movimentações financeiras
      */
     async getRelatorioMovimentacoes(organizationId: number, limite: number = 100): Promise<PostgrestResponse<IVwMovimentacoesRecentes[]>> {
          return await this.client
               .from('vw_movimentacoes_recentes')
               .select('*')
               .eq('fk_id_organizacao', organizationId)
               .order('data_movimentacao', { ascending: false })
               .limit(limite);
     }


     /**
      * Estatísticas rápidas para o dashboard
      */
     async getEstatisticasRapidas(organizationId: number): Promise<EstatisticasRapidas> {
          // Usar views para cálculos pré-processados no banco
          const [
               totalVendasHoje,
               totalResponsaveis,
               totalAlunos,
               produtosDisponiveis
          ] = await Promise.all([
               this.client
                    .from('vw_vendas_hoje')
                    .select('valor_final.sum()')
                    .eq('organizacao', organizationId),
               this.client
                    .from('vw_saldos_responsaveis')
                    .select('id_responsavel')
                    .eq('organizacao', organizationId),
               this.client
                    .from('vw_saldos_alunos')
                    .select('id_aluno')
                    .eq('organizacao', organizationId),
               this.client
                    .from('vw_produtos_estoque')
                    .select('id_produto')
                    .eq('organizacao', organizationId)
          ]);

          return {
               vendas_hoje: totalVendasHoje.data?.[0]?.sum || 0,
               total_responsaveis: totalResponsaveis.data?.length || 0,
               total_alunos: totalAlunos.data?.length || 0,
               produtos_disponiveis: produtosDisponiveis.data?.length || 0
          };
     }

     /**
      * Exportação de dados para relatórios externos
      */
     async exportarDados(organizationId: number, tipo: 'vendas' | 'saldos' | 'produtos' | 'movimentacoes'): Promise<PostgrestResponse<any[]>> {
          const viewMap = {
               vendas: 'vw_vendas_hoje',
               saldos: 'vw_saldos_responsaveis',
               produtos: 'vw_produtos_estoque',
               movimentacoes: 'vw_movimentacoes_recentes'
          };

          const viewName = viewMap[tipo];
          if (!viewName) {
               throw new Error('Tipo de relatório inválido');
          }

          if (viewName === 'vw_movimentacoes_recentes') {
               return await this.client
                    .from(viewName)
                    .select('*')
                    .eq('fk_id_organizacao', organizationId);
          }
          return await this.client
               .from(viewName)
               .select('*')
               .eq('organizacao', organizationId);
     }
}

export const viewRelatorios = new ViewRelatorios();
